import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { UserRole } from '@/types/enums'
import type { Profile } from '@/types'

export function useAuth() {
  const { user, profile, isLoading, setUser, setProfile, setLoading, reset } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
      } else {
        setProfile(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function fetchProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Failed to fetch profile:', error)
      setProfile(null)
    } else {
      setProfile(data as Profile)
    }
    setLoading(false)
  }

  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  const register = useCallback(async (
    email: string,
    password: string,
    fullName: string,
    role: UserRole = UserRole.Client
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, role },
      },
    })
    if (error) throw error
    return data
  }, [])

  const logout = useCallback(async () => {
    await supabase.auth.signOut()
    reset()
    navigate('/login')
  }, [navigate, reset])

  const redirectByRole = useCallback((userProfile: Profile) => {
    switch (userProfile.role) {
      case UserRole.Admin:
        navigate('/admin/dashboard')
        break
      case UserRole.Agent:
        navigate('/agent/dashboard')
        break
      case UserRole.Client:
        navigate('/client/dashboard')
        break
      default:
        navigate('/login')
    }
  }, [navigate])

  return {
    user,
    profile,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: profile?.role === UserRole.Admin,
    isAgent: profile?.role === UserRole.Agent || profile?.role === UserRole.Admin,
    isClient: profile?.role === UserRole.Client,
    login,
    register,
    logout,
    redirectByRole,
  }
}
